import React, { useCallback, useEffect, useState } from "react";

import { getAdminFarmPayoutApprovals, getFarmVerificationSummary, getFieldModuleVerificationSummary, getFieldVerificationSummary } from "services";

import {
  getAdminVerificationYearlyDataYear,
  useCacheInvalidation,
  useCancelAsyncOnUnmount,
  useGetCarbonProgramByFarmId,
  useGetModules,
  useParams,
} from "utils";

import { MainPage, TabLabel, Tabs } from "components";

import FootprintingVerificationsAssessmentList from "pages/admin/verifications/components/footprinting/FootprintVerificationsAssessmentList";
import { CropList } from "pages/admin/verifications/components/lists/CropList";
import { FertilizerList } from "pages/admin/verifications/components/lists/FertilizerList";
import { FieldList } from "pages/admin/verifications/components/lists/FieldList";
import { TillageAndSeedingList } from "pages/admin/verifications/components/lists/TillageAndSeedingList";
import { Documents } from "pages/admin/verifications/components/misc/Documents";
import { FarmHeader } from "pages/admin/verifications/components/misc/FarmHeader";
import { FarmOverview } from "pages/admin/verifications/components/misc/FarmOverview";
import { PayoutHistory } from "pages/admin/verifications/components/misc/PayoutHistory";

const MODULE_ORDER_BY_ID = [
  "5ef487b9-272e-4ec1-bab0-532c8a447521", // Crop rotation
  "cccb8cfc-7b3c-48f8-b590-ce7f28dcb1e1", // Fertilizer
  "8dfe7438-edad-4893-84f4-689a1ea862a5", // Tillage
  "0d5e54d5-6fa1-44fc-9940-db76185bf2d9", // Pesticides
  "1d932e3f-0695-40b7-9eac-774c8871a81e", // Dairy
];

export const Farm = () => {
  const { farmId, section } = useParams();
  const { data: modules } = useGetModules();

  const [activeTab, setActiveTab] = useState(section);
  const [farm, setFarm] = useState();
  const [farmVerificationSummary, setFarmVerificationSummary] = useState();
  const [, setFieldModuleVerificationSummaries] = useState({});
  const [fieldVerificationSummary, setFieldVerificationSummary] = useState();
  const [loadingFarm, setLoadingFarm] = useState(true);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [payoutApprovals, setPayoutApprovals] = useState([]);
  const [userModules, setUserModules] = useState([]);
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);

  useCacheInvalidation(true, farm?.userId);

  const signal = useCancelAsyncOnUnmount()();

  const loadTotalFarmVerificationSummary = useCallback(() => {
    getFarmVerificationSummary({
      farmId,
      signal,
    })
      .then(({ farm, modules, ...summary }) => {
        setFarmVerificationSummary(summary);
        setFarm(farm);

        const userModules = modules.sort((moduleIdA, moduleIdB) => {
          const moduleIndexA = MODULE_ORDER_BY_ID.indexOf(moduleIdA);
          const moduleIndexB = MODULE_ORDER_BY_ID.indexOf(moduleIdB);

          if (!moduleIdA || !moduleIdB) return 0;

          return moduleIndexA - moduleIndexB;
        });

        setUserModules(userModules);
        setUserModules(userModules);
      })
      .finally(() => {
        setLoadingFarm(false);
      });
  }, [farmId, signal]);

  const loadFieldVerificationSummary = useCallback(() => {
    getFieldVerificationSummary({
      farmId,
      signal,
    }).then((res) => {
      setFieldVerificationSummary(res);
    });
  }, [farmId, signal]);

  const loadFieldModuleVerificationSummary = useCallback(
    (moduleId) => {
      getFieldModuleVerificationSummary({
        farmId,
        moduleId,
        signal,
      }).then(({ years, ...res }) => {
        setFieldModuleVerificationSummaries((prev) => ({ ...prev, [moduleId]: { years, summary: res } }));
      });
    },
    [farmId, signal]
  );

  const loadFarmPayoutApprovals = useCallback(() => {
    setLoadingPayouts(true);
    getAdminFarmPayoutApprovals({
      farmId,
      signal,
    })
      .then((res) => setPayoutApprovals(res))
      .finally(() => setLoadingPayouts(false));
  }, [signal, farmId]);

  useEffect(() => {
    loadFieldVerificationSummary();
  }, [loadFieldVerificationSummary]);

  useEffect(() => {
    setLoadingFarm(true);
    loadTotalFarmVerificationSummary();
  }, [loadTotalFarmVerificationSummary]);

  useEffect(() => {
    loadFarmPayoutApprovals();
  }, [loadFarmPayoutApprovals]);

  useEffect(() => {
    if (modules?.length) {
      userModules.forEach((id) => loadFieldModuleVerificationSummary(id));
    }
  }, [userModules, modules, loadFieldModuleVerificationSummary]);

  const verificationYear = getAdminVerificationYearlyDataYear();
  const hasCurrentYearVerificationApproval = !!payoutApprovals?.filter(({ year }) => year === verificationYear).length;

  // const handlePayoutApproval = () => {
  //   setLoadingFarm(true);
  //   loadTotalFarmVerificationSummary();
  //   loadFarmPayoutApprovals();
  // };

  return (
    <MainPage pageName="admin" loading={loadingPayouts || loadingFarm}>
      {(section === "dashboard" ||
        section === "documents" ||
        section === "farm" ||
        section === "fields" ||
        section === "crops" ||
        section === "fertilizers" ||
        section === "tillage-and-seeding" ||
        section === "footprinting" ||
        userModules.length) && (
        <>
          <FarmHeader
            farm={farm}
            userId={farm?.userId}
            onUpdate={() => loadTotalFarmVerificationSummary()}
            taskStatus={farmVerificationSummary?.verificationTaskStatus}
            loadingFarm={loadingFarm || !farm}
            verificationSummary={farmVerificationSummary}
          />
          <Tabs
            onChange={(e) => setActiveTab(e)}
            activeKey={activeTab}
            defaultActiveKey={section || activeTab || "dashboard"}
            items={[
              !hasCurrentYearVerificationApproval && !loadingPayouts
                ? {
                    label: (
                      <TabLabel dotColor="red" showDot={farm?.verified === false}>
                        dashboard
                      </TabLabel>
                    ),
                    key: "dashboard",
                    children: <p>Work in progress</p>,
                  }
                : null,
              {
                label: <TabLabel>documents</TabLabel>,
                key: "documents",
                children: <Documents farmId={farmId} farmCountry={farm?.country} farmName={farm?.name} />,
              },
              {
                label: (
                  <TabLabel dotColor="yellow" showDot={farm?.verified === null}>
                    farm
                  </TabLabel>
                ),
                key: "farm",
                children: (
                  <FarmOverview
                    farm={farm}
                    farmId={farmId}
                    modules={modules}
                    onUpdate={() => loadTotalFarmVerificationSummary()}
                    userModules={userModules}
                    points={farmVerificationSummary?.points}
                  />
                ),
              },
              {
                label: (
                  <TabLabel dotColor="yellow" showDot={fieldVerificationSummary?.fieldPendingVerificationCount > 0}>
                    fields
                  </TabLabel>
                ),
                key: "fields",
                children: (
                  <FieldList
                    farm={farm}
                    farmId={farmId}
                    onUpdate={() => {
                      loadFieldVerificationSummary();
                      loadTotalFarmVerificationSummary();
                    }}
                    summary={fieldVerificationSummary}
                    payoutApproved={hasCurrentYearVerificationApproval}
                  />
                ),
              },
              {
                label: (
                  // TODO: show dot again if pending
                  <TabLabel dotColor="yellow" showDot={false}>
                    crops
                  </TabLabel>
                ),
                key: "crops",
                children: <CropList farmId={farmId} />,
              },
              {
                label: (
                  <TabLabel dotColor="yellow" showDot={false}>
                    fertilizers
                  </TabLabel>
                ),
                key: "fertilizers",
                children: <FertilizerList farmId={farmId} />,
              },
              {
                label: (
                  <TabLabel dotColor="yellow" showDot={false}>
                    tillage & seeding
                  </TabLabel>
                ),
                key: "tillage-and-seeding",
                children: <TillageAndSeedingList farmId={farmId} />,
              },
              carbonProgram?.platformFeatures?.footprinting?.enabled
                ? {
                    label: <TabLabel>footprinting</TabLabel>,
                    key: "footprinting",
                    children: <FootprintingVerificationsAssessmentList farmId={farmId} />,
                  }
                : null,
              hasCurrentYearVerificationApproval
                ? {
                    label: <TabLabel>payout history</TabLabel>,
                    key: "payout",
                    children: (
                      <PayoutHistory payouts={payoutApprovals} userModules={userModules.map((moduleId) => modules.find(({ id }) => id === moduleId))} />
                    ),
                  }
                : null,
            ]}
          />
        </>
      )}
    </MainPage>
  );
};
